// import { OptionsIcon } from '@components/Icons/OptionsIcon';
import { PlayIcon } from '@components/Icons/PlayIcon';
import { Album, PostDataItem } from 'src/types';
// import { RightArrowIcon2 } from '@components/Icons/RightArrowIcon2';
// import { CommentIcon } from '@components/Icons/CommentIcon.';
import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
// import { SavedIcon } from '@components/Icons/SavedIcon';
import Loader from '@components/Loader';
import { useCommonState } from '@hooks/storeHooks/useCommonState';
import { useMusicState } from '@hooks/storeHooks/useMusicState';
import useAlbumData from '@hooks/useAlbumData';
import { useCallback, useState } from 'react';
import { placeHolderData } from '../../../../config';
import { getTrackUrlForLocale } from '../../../../utils/localeUtils';
import DescriptionWithLinks from './DescriptionWithLinks';

/** Matches contributor album cards / Feeds demo: portrait thumb for audiobooks. */
export const FEED_ALBUM_THUMB_PORTRAIT = 'w-[200px] h-[300px]';

interface IProps {
  bgStyle: string;
  data: PostDataItem & { isLiked?: boolean };
  widthHeight: string;
  onLikesUpdate?: (albumId: string, newLikes: number, isLiked: boolean) => void;
  onPlayClick?: () => void; // Optional custom play click handler (for creations)
}

export const FeedPostStyle = (props: IProps) => {
  const { bgStyle, data, widthHeight, onLikesUpdate, onPlayClick } = props;
  const isAudiobookPortrait = widthHeight === FEED_ALBUM_THUMB_PORTRAIT;
  const { setActivePlaylist, addRecentTrack, preferredLocale } =
    useMusicState();
  const { setActivePopup } = useCommonState();
  const { addFavouriteItem, loadAlbumById } = useAlbumData();

  const [isLoading, setIsLoading] = useState(false);
  const [likes, setLikes] = useState<string>(data.likes || '0');
  const [isLiked, setIsLiked] = useState<boolean>(data.isLiked ?? false);

  // Handle music player opening - fetch data on demand
  const playPlaylist = useCallback(async () => {
    // If custom play handler provided (e.g., for creations), use it
    if (onPlayClick) {
      onPlayClick();
      return;
    }

    if (!data?.albumId) {
      console.warn('No album ID available');
      return;
    }

    setIsLoading(true);

    try {
      const albumData = await loadAlbumById(data.albumId);
      openMusicPlayerPage(albumData);
    } catch (err) {
      console.error('Error loading album for playback:', err);
    } finally {
      setIsLoading(false);
    }
  }, [data?.albumId, loadAlbumById, onPlayClick]);

  // Handle music player opening
  const openMusicPlayerPage = useCallback(
    (albumData: Album) => {
      const contributor = albumData.contributors?.[0];
      const newActivePlaylist = {
        currentIndex: 0,
        album: albumData,
      };

      // Add first track to recent tracks when album is played
      if (albumData.tracks && albumData.tracks.length > 0) {
        const firstTrack = albumData.tracks[0];
        const recentTrack = {
          track: {
            name: firstTrack.name,
            url: getTrackUrlForLocale(firstTrack, preferredLocale),
            trackIndex: 1,
          },
          contributor: {
            id: albumData.id,
            givenName: contributor?.givenName || 'Unknown Artist',
            thumbnailUrl:
              contributor?.thumbnailUrl || placeHolderData.contributorThumbnail,
          },
          albumArtUrl:
            albumData.albumArtUrl ||
            contributor?.thumbnailUrl ||
            placeHolderData.albumCover,
          timestamp: Date.now(),
        };
        addRecentTrack(recentTrack);
      }

      // Store current page as previous page before opening music player
      setActivePlaylist(newActivePlaylist);
      setActivePopup('musicplayer');
    },
    [setActivePlaylist, setActivePopup, addRecentTrack],
  );
  return (
    <div
      className={`w-full ${bgStyle} rounded-[16px] pb-[16px] font-[NapsterSans]`}>
      <div className="flex p-4 w-full gap-2 items-center">
        <div className="flex w-[36px] h-[36px]">
          <img
            className="w-full h-full object-fit-cover"
            src={
              data.contributorImage
                ? data.contributorImage
                : '/assets/images/logged_user.png'
            }
            alt="User"
          />
        </div>
        <div className="flex flex-col flex-1">
          <div className="flex text-[14px] font-semibold leading-[130%] text-left text-white pb-[2px] font-[NapsterSans]">
            {data.name}
          </div>
          <div className="flex gap-[5px] items-center font-[NapsterSans]">
            <div className="flex text-[12px] font-normal leading-[130%] text-left text-white/60 pb-[2px]">
              {data.username}
            </div>
            <div className="flex text-[12px] font-normal leading-[130%] text-left text-white/60 font-[NapsterSans]">
              • {data.time}
            </div>
            <div className="flex">
              {' '}
              <span className="cursor-default text-[8px] font-normal leading-[130%] text-left text-white/60 border border-white/20 p-[3px] rounded-[4px] font-[NapsterSans]">
                {data.category}
              </span>
            </div>
          </div>
        </div>
        <div className="flex">{/* <OptionsIcon opacityValue={0.8} /> */}</div>
      </div>

      <div className="w-full p-4 pt-[0] text-[14px] font-normal leading-[130%] text-left text-white">
        <DescriptionWithLinks description={data.description} />
      </div>

      <div className="w-full px-4 pb-4">
        <div className="w-full">
          <div className="w-full flex items-center justify-center">
            <div
              className={`${widthHeight}  shadow-[0_4px_32px_0_rgba(0,0,0,0.4)] rounded-[8px] overflow-hidden relative z-[13]`}>
              <img
                className={`w-full h-full object-cover${isAudiobookPortrait ? ' object-center' : ''}`}
                src={data.postImage || '/assets/images/feed.png'}
                alt={data.albumName || 'Album Cover'}
              />
              <div
                className="w-[40px] h-[40px] absolute left-[12px] bottom-[12px] flex items-center justify-center bg-[rgba(0,0,0,0.2)] cursor-pointer hover:bg-[rgba(0,0,0,0.4)] transition-colors"
                onClick={playPlaylist}>
                {isLoading ? (
                  <Loader size="small" />
                ) : (
                  <PlayIcon width={24} height={24} />
                )}
              </div>
              {/* <div className="w-[40px] h-[40px] absolute right-[12px] bottom-[12px] flex items-center justify-center bg-[rgba(0,0,0,0.2)]">
                <RightArrowIcon2 />
              </div> */}
            </div>
          </div>

          <div
            className={`w-full ${bgStyle} relative z-[11] min-h-[200px] rounded-t-[8px] ${
              isAudiobookPortrait
                ? 'mt-[-150px] pt-[180px]'
                : 'mt-[-100px] pt-[130px]'
            }`}>
            <div className="w-full text-[12px] font-medium leading-[130%] text-white text-center pb-3">
              {data.albumName}, Napster
            </div>
          </div>

          <div className="flex w-full gap-[4px] relative z-[1] ">
            <button
              className="flex items-center gap-[4px] h-[28px] border-1 border-white/20 w-fit px-3 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
              onClick={() => {
                if (data?.albumId) {
                  addFavouriteItem('album', data.albumId, isLiked);
                  // Update likes count optimistically
                  const currentLikes = parseInt(likes) || 0;
                  const newLikes = isLiked
                    ? currentLikes - 1
                    : currentLikes + 1;
                  const newIsLiked = !isLiked;
                  setLikes(newLikes.toString());
                  setIsLiked(newIsLiked);
                  onLikesUpdate?.(data.albumId, newLikes, newIsLiked);
                }
              }}>
              {isLiked ? (
                <Likedicon width={16} height={16} />
              ) : (
                <FavoriteIcon width={16} height={16} fillOpacity={0.8} />
              )}
              {/* <span className="text-[10px] font-normal leading-[140%] text-white text-left">
                {likes || '0'}
              </span> */}
            </button>

            {/* <button className="flex items-center gap-[4px] h-[28px] border-1 border-white/20 w-fit px-3 rounded-full">
              <CommentIcon />
              <span className="text-[10px] font-normal leading-[140%] text-white text-left">
                4.1K
              </span>
            </button> */}

            {/* <button className="flex items-center gap-[4px] h-[28px] border-1 border-white/20 w-fit px-3 rounded-full">
              <SavedIcon />

              <span className="text-[10px] font-normal leading-[140%] text-white text-left">
                4.1K
              </span>
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};
